import { UTCDateMini } from '@date-fns/utc';
import { Box, Button, Option, Select, Sheet, Stack, Typography } from '@healthinal/ui';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getMonth,
  getYear,
  isSameDay,
  isSameMonth,
  isToday,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { useEffect, useMemo, useState } from 'react';
import { formatDateForInput, getCurrentDate } from '../../utils/date.ts';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;
const MONTHS = Array.from({ length: 12 }, (_, month) =>
  format(new UTCDateMini(2020, month, 1), 'LLLL', { locale: de }),
);
const DEFAULT_PAST_YEARS = 300;
const DEFAULT_FUTURE_YEARS = 150;
const SELECT_LISTBOX_PROPS = {
  'data-german-date-calendar-listbox': 'true',
  placement: 'bottom-start' as const,
  modifiers: [
    {
      name: 'flip',
      enabled: false,
    },
  ],
  sx: {
    zIndex: 1700,
    maxHeight: 260,
  },
};

interface GermanDateCalendarProps {
  value?: string;
  min?: string;
  max?: string;
  onChange: (value: string) => void;
  onClear: () => void;
}

function toDate(value?: string) {
  return value ? new UTCDateMini(value) : undefined;
}

function isDayDisabled(day: Date, min?: string, max?: string) {
  const minDate = toDate(min);
  const maxDate = toDate(max);
  if (minDate && day < minDate) return true;
  if (maxDate && day > maxDate) return true;
  return false;
}

function getYearOptions(viewYear: number, min?: string, max?: string) {
  const currentYear = getYear(new UTCDateMini(getCurrentDate()));
  const minYear = toDate(min)?.getFullYear() ?? currentYear - DEFAULT_PAST_YEARS;
  const maxYear = toDate(max)?.getFullYear() ?? currentYear + DEFAULT_FUTURE_YEARS;
  const firstYear = Math.min(minYear, maxYear, viewYear);
  const lastYear = Math.max(minYear, maxYear, viewYear);

  return Array.from({ length: lastYear - firstYear + 1 }, (_, index) => firstYear + index);
}

export function GermanDateCalendar({ value, min, max, onChange, onClear }: GermanDateCalendarProps) {
  const selectedDate = toDate(value);
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    startOfMonth(selectedDate ?? new UTCDateMini(getCurrentDate())),
  );

  useEffect(() => {
    setViewMonth(startOfMonth(selectedDate ?? new UTCDateMini(getCurrentDate())));
  }, [value]);

  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd }).map((day) => new UTCDateMini(day));
  const viewMonthIndex = getMonth(viewMonth);
  const viewYear = getYear(viewMonth);
  const yearOptions = useMemo(() => getYearOptions(viewYear, min, max), [max, min, viewYear]);

  return (
    <Sheet variant="outlined" sx={{ p: 1.5, minWidth: 280 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Select
          size="sm"
          variant="plain"
          value={viewMonthIndex}
          onChange={(_, month) => {
            if (month !== null) setViewMonth((currentMonth) => startOfMonth(setMonth(currentMonth, month)));
          }}
          slotProps={{ listbox: SELECT_LISTBOX_PROPS }}
          sx={{ flex: 1, textTransform: 'capitalize' }}>
          {MONTHS.map((month, index) => (
            <Option key={month} value={index}>
              {month}
            </Option>
          ))}
        </Select>
        <Select
          size="sm"
          variant="plain"
          value={viewYear}
          onChange={(_, year) => {
            if (year !== null) setViewMonth((currentMonth) => startOfMonth(setYear(currentMonth, year)));
          }}
          slotProps={{ listbox: SELECT_LISTBOX_PROPS }}
          sx={{ width: 96 }}>
          {yearOptions.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
          textAlign: 'center',
          mb: 1,
        }}>
        {WEEKDAYS.map((weekday) => (
          <Typography key={weekday} level="body-xs" sx={{ color: 'text.tertiary', py: 0.5 }}>
            {weekday}
          </Typography>
        ))}
        {days.map((day) => {
          const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
          const isOutsideMonth = !isSameMonth(day, viewMonth);
          const disabled = isDayDisabled(day, min, max);

          return (
            <Button
              key={day.toISOString()}
              size="sm"
              variant={isSelected ? 'solid' : 'plain'}
              color={isSelected ? 'primary' : 'neutral'}
              disabled={disabled}
              onClick={() => onChange(formatDateForInput(day))}
              sx={{
                minWidth: 0,
                px: 0,
                opacity: isOutsideMonth ? 0.45 : 1,
                fontWeight: isToday(day) && !isSelected ? 'lg' : undefined,
              }}>
              {format(day, 'd')}
            </Button>
          );
        })}
      </Box>

      <Stack direction="row" justifyContent="space-between">
        <Button size="sm" variant="plain" onClick={onClear}>
          Löschen
        </Button>
        <Button
          size="sm"
          variant="plain"
          disabled={isDayDisabled(new UTCDateMini(getCurrentDate()), min, max)}
          onClick={() => onChange(getCurrentDate())}>
          Heute
        </Button>
      </Stack>
    </Sheet>
  );
}
